import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useMemo, useRef} from 'react';

import * as analytics from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {FixedPlateModalStep} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Pagination} from 'lib/Listings';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import {
  FillPlateModalReducerActionType,
  FillPlateModalReducerDispatch,
} from 'samples/plate-maps/fill-plate-modal/useFillPlateModalReducer';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  DisplayLayerMetadata,
  PlateMap,
  PlateMaterialInput,
  PlateModalAnalyticsMetadata,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SupportedAdditionalAttribute,
  TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {
  getDisplayLayerAttributeKey,
  getDisplayLayerForAttribute,
  isWellEmpty,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import FillPlateTransferTable from 'samples/plate-maps/transfer-table/FillPlateTransferTable';
import {FillPlateTransferTableViewMode} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT} from 'ui-library/Colors';

export interface FillPlateTransferStepProps {
  /** A function to dispatch change events to update the state props. */
  dispatch: FillPlateModalReducerDispatch;
  /** Information about how to page through the selected wells. */
  pagination: Pagination;
  /** The plate map representing the plate being filled */
  plateMap: PlateMap;
  /** In a procedure context, a list of plate-used material inputs. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /** Groupings of reagents to be filled into the plate */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Whether the table is looking at the plate as a whole (i.e. the reagent schemas) or each well in the plate. */
  viewMode: FillPlateTransferTableViewMode;
  /** Ref for the modal displaying this step */
  modalRef?: RefObject<HTMLDivElement>;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
  /** Max height of the plate view in this step, determined by the parent modal */
  maxPlateViewHeight: string;
  /** Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  plateMapType: PlateMapType;
  /** Plate data parameter layers, only applicable in a procedures context. */
  plateDataParametersOptions?: Array<DisplayLayerMetadata>;
  /** The container schema associated with the plate schema. */
  containerSchemaId: string;
}

/**
 * Renders a fill plate modal step for setting transfer volumes and concentrations for reagents being
 * transferred into a plate.
 * @constructor
 */
export default function FillPlateTransferStep({
  dispatch,
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  viewMode,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  procedureStudyDesignType,
  plateMapType,
  plateDataParametersOptions,
  containerSchemaId,
}: FillPlateTransferStepProps): JSX.Element {
  const mapPaneWrapperRef = useRef<HTMLDivElement>(null);
  const tablePaneRef = useRef<HTMLDivElement>(null);
  const helpTextRef = useRef<HTMLDivElement>(null);

  const isEmpty = useMemo(() => plateMap.wells.every((well) => isWellEmpty(well)), [plateMap.wells]);
  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute} =
    usePlateMapDisplayAttributeState(
      plateMap,
      isEmpty ? WELL_ROLE_ATTRIBUTE_SENTINEL : REAGENTS_ATTRIBUTE_SENTINEL,
      true,
      isEmpty ? undefined : {[REAGENTS_ATTRIBUTE_SENTINEL]: reagentGroupsById}
    );

  const baseAttributeOptions: Array<SupportedAdditionalAttribute> = [
    REAGENTS_ATTRIBUTE_SENTINEL,
    WELL_ROLE_ATTRIBUTE_SENTINEL,
  ];
  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'FillPlateTransferStep', element: 'root'})}
    >
      <div className={css(styles.mapPaneWrapper)} ref={mapPaneWrapperRef}>
        <PlateMapPane
          header={
            <div className={css(styles.layerDropdown)}>
              <PlateMapDisplayLayerDropdown
                containerSchemaId={containerSchemaId}
                additionalAttributeOptions={
                  shouldShowTestOrderDilutionInfo(procedureStudyDesignType, plateMapType)
                    ? [...baseAttributeOptions, TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL]
                    : baseAttributeOptions
                }
                plateDataParametersOptions={plateDataParametersOptions}
                selectedAttribute={selectedAttribute}
                setSelectedAttribute={setSelectedAttribute}
              />
            </div>
          }
          plateMapDisplayParams={{
            plateMap,
            displayLayer: getDisplayLayerForAttribute(selectedAttribute),
            attribute: selectedAttribute,
            assignmentGroupsById:
              assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
            groupedValidationErrors: {},
          }}
          isPlateMapViewOnly={isEmpty}
          selectedWellIndices={selectedWellIndices}
          setSelectedWellIndices={(selectedWells: Set<number>) => {
            analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.CHANGED_CELL_SELECTION, {
              ...analyticsMetadata,
              step: FixedPlateModalStep.TRANSFER_QUANTITIES_STEP,
            });
            dispatch({type: FillPlateModalReducerActionType.SET_SELECTED_WELLS, selectedWells});
          }}
          previewedWellIndices={new Set<number>()}
          deselectWellsOnClickRefs={[mapPaneWrapperRef, tablePaneRef, helpTextRef]}
          modalRef={modalRef}
          maxPlateViewHeight={maxPlateViewHeight}
          showFinalMeasurements={true}
          plateMapType={plateMapType}
        />
      </div>
      <div className={css(styles.border)} />
      <div className={css(styles.tablePane)} ref={tablePaneRef}>
        <FillPlateTransferTable
          dispatch={dispatch}
          pagination={pagination}
          plateMap={plateMap}
          plateMaterialInputs={plateMaterialInputs}
          reagentGroupsById={reagentGroupsById}
          selectedWellIndices={selectedWellIndices}
          viewMode={viewMode}
          isEmpty={isEmpty}
          analyticsMetadata={analyticsMetadata}
          helpTextRef={helpTextRef}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    gap: 16,
    maxHeight: 'calc(100vh - 280px)',
  },
  // This is a border between the plate view and the search view. We want it to reach all the way to the top
  // and bottom of the modal body, which has a 16px padding on all sides.
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    marginTop: '-16px',
    marginBottom: '-16px',
  },
  mapPaneWrapper: {
    width: '33%',
    overflow: 'auto',
    display: 'flex',
  },
  tablePane: {
    flex: 1,
    overflow: 'auto',
  },
  layerDropdown: {
    maxWidth: '264px',
  },
});
